<template>
	<Modals
		:title="'Details'"
		:display="true"
		@show="close"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
	>
		<template #modal-content>
			<div class="detail-update">
				{{ formatChangedByAndDatetimeLog }}
			</div>
			<Table
				class="table-container--features-customer"
				:sourceObject="customersLogData"
				:tableMobilev1="true"
				@change="changedPagination"
			>
				<template #col-group>
					<colgroup>
						<col :class="['table__col table__col--setting']" />
						<col :class="['table__col table__col--change']" />
					</colgroup>
				</template>
				<template #body-changedPropertyName="{body}">
					<div class="customer-detail">
						<strong
							>{{ body.changedPropertyName.customer }}
						</strong>

						<ul class="companies-list">
							<li
								v-for="company in body.changedPropertyName
									.companies"
								:key="company"
							>
								{{ company }}
							</li>
						</ul>
					</div>
				</template>
				<template #body-newValue="{body}">
					<div class="customer-detail">
						<span
							class="customer-detail__value"
							v-html="body.newValue"
						/>
					</div>
				</template>
			</Table>
		</template>
		<template #modal-footer>
			<Button
				className="btn--primary"
				labelText="Close"
				:btnClick="close"
			/>
		</template>
	</Modals>
</template>

<script>
import Modals from '@/components/shells/modal/modals';
import Table from '@/components/shells/table/table';
import DateTimeMixin from '@/mixins/datetime-mixin';
import { updatePagination } from '@/utils/object';
import Button from '@/components/forms/button/button';
import _ from 'lodash';

export default {
	name: 'FeaturesCustomersModal',
	components: {
		Modals,
		Table,
		Button
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		parentTag: {
			type: String,
			default: 'div'
		},
		display: {
			type: Boolean,
			default: false
		},
		logs: {
			type: Object,
			default: () => {}
		}
	},
	mixins: [DateTimeMixin],
	data() {
		return {
			featureCustomerHeader: [
				{
					key: 'changedPropertyName',
					label: 'Client/Customer/Sublocation',
					sorteable: false
				},
				{
					key: 'newValue',
					label: 'Change',
					sorteable: false
				}
			],
			customersLogData: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			page: 1,
			pageSize: 10,
			loading: false
		};
	},
	created() {
		this.customersLogData.headers = [...this.featureCustomerHeader];
		this.fetchBetaFeature();
	},
	computed: {
		formatChangedByAndDatetimeLog() {
			if (_.isEmpty(this.logs)) {
				return '';
			}
			const { timestamp, changedByUserName, action } = this.logs;

			const name = this.capitalizeWords(changedByUserName);
			const formattedDate = this.shortDateString(timestamp);
			const formattedTime = this.timeAndTimeZone(timestamp);

			return `${action}d by ${name} on ${formattedDate} at ${formattedTime}`;
		}
	},
	methods: {
		capitalizeWords(str) {
			return str
				.split(' ')
				.map(
					word =>
						word.charAt(0).toUpperCase() +
						word.slice(1).toLowerCase()
				)
				.join(' ');
		},
		close() {
			this.loading = false;
			this.$emit('close', false);
		},
		changedPagination(value) {
			this.page = value.page;
			this.pageSize = value.countPerPage;
		},
		fetchBetaFeature() {
			let newSource = updatePagination(
				this.page,
				this.pageSize,
				_.cloneDeep(this.parseFeatureLogs())
			);
			this.customersLogData.source = newSource;
		},
		getCustomerData(data) {
			const match = data.match(
				/CustomerName:([^,]+),CustomerNumber:([^,\]]+)/
			);
			return match ? `${match[2].trim()} - ${match[1].trim()}` : '';
		},

		processCompanyData(value) {
			const allCompaniesActive =
				value.TotalCompanies === value.ActiveCompanies.length;
			return allCompaniesActive
				? ['All Companies / Sublocations']
				: value.ActiveCompanies.map(
						c => `(${c.CompanyNumber}) ${c.CompanyName}`
				  );
		},
		processFieldChangeItem(item) {
			const value =
				item.Action === 'Add' ? item.NewValue : item.OriginalValue;
			const hasCompany = item.ChangedPropertyName.includes(
				'ActiveCompanies'
			);

			if (hasCompany) {
				return {
					customer: this.getCustomerData(item.ChangedPropertyName),
					companies: [`(${value.CompanyNumber}) ${value.CompanyName}`]
				};
			}

			return {
				customer: `${value.CustomerNumber} - ${value.CustomerName}`,
				companies: this.processCompanyData(value)
			};
		},
		groupByActionAndCustomer(data) {
			return Object.values(
				data.reduce(
					(
						acc,
						{
							newValue,
							changedPropertyName: { customer, companies }
						}
					) => {
						const key = `${newValue}-${customer}`;

						if (!acc[key]) {
							acc[key] = {
								newValue,
								changedPropertyName: {
									customer,
									companies: [...companies]
								}
							};
						} else {
							acc[key].changedPropertyName.companies = [
								...new Set([
									...acc[key].changedPropertyName.companies,
									...companies
								])
							];
						}

						return acc;
					},
					{}
				)
			);
		},
		parseFeatureLogs() {
			const { fieldChanges } = this.logs;

			if (_.get(fieldChanges, 'items.length', 0) === 0) {
				return [];
			}

			const actionLabels = { Add: 'Added', Delete: 'Removed' };
			const items = fieldChanges.items.map(item => {
				const processed = this.processFieldChangeItem(item);
				return {
					changedPropertyName: {
						customer: processed.customer,
						companies: processed.companies
					},
					newValue: actionLabels[item.Action]
				};
			});

			const groupByItems = this.groupByActionAndCustomer(items);

			return _.orderBy(
				groupByItems,
				[
					item => item.newValue.toLowerCase(),
					item => item.changedPropertyName.customer.toLowerCase()
				],
				['asc', 'asc']
			);
		}
	},
	watch: {
		page: {
			immediate: true,
			handler() {
				this.fetchBetaFeature();
			}
		},
		pageSize: {
			immediate: true,
			handler() {
				this.fetchBetaFeature();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.detail-update {
	font-family: $font-family;
	font-size: $bd-txt-font-size-sm;
	color: $neutral-2;
	margin-bottom: $gutter-xl;
}
.table-container--features-customer {
	.table__col {
		&--setting {
			width: 400px;
		}
		&--change {
			width: 200px;
			text-transform: lowercase;
		}
	}
}
.customer-detail {
	font-family: $secondary-font-family;

	&__value {
		text-transform: none;
	}
}
.companies-list {
	font-family: $font-family;
}
</style>